import { rentalInvoicePaymentReferences } from './constants'
import { TPaymentRule, TPortfolio, TPortfolioPartyGroup, TPortfolioSplit } from './types'

/**
 * Every `partyId` on a portfolio's tenants and owners, primary and secondary.
 */
export const getTenantAndLandlordIdsFromPortfolio = (portfolio?: TPortfolio): string[] => {
  const parties = portfolio?.parties
  return [...getAllPartyIds(parties?.tenants), ...getAllPartyIds(parties?.owners)]
}

/**
 * Check if a portfolio is rolling over month to month
 */
export const isPortfolioMonthToMonth = (portfolio?: { leaseTerms?: { rolloverMonthToMonth?: boolean } }): boolean =>
  portfolio?.leaseTerms?.rolloverMonthToMonth ?? false

/**
 * Get the total amount of all payment rules, excluding rent
 *
 * @param paymentRules list of payment rules
 * @param commissionAmount commission amount value, in cents
 * @returns summed value of all payment rule amounts, in cents
 */
export const getPaymentRulesAmountExRent = (
  paymentRules: TPaymentRule[],
  /** The commission fields hand this over as a string, so coerce rather than concatenate. */
  commissionAmount: number | string,
): number =>
  paymentRules
    .filter(pr => {
      const value = pr.beneficiary.value as { partyTag?: string; reference?: string }
      return (
        value.partyTag !== 'Owner' &&
        value.reference !== rentalInvoicePaymentReferences.rentalIncome &&
        value.reference !== rentalInvoicePaymentReferences.commission
      )
    })
    .reduce((total, pr) => total + (pr.beneficiary.value.amount ?? 0), Number(commissionAmount) || 0)

export const getAllPartyIds = (parties?: TPortfolioPartyGroup): string[] => {
  if (!parties) {
    return []
  }
  const primaryId = parties.primary?.partyId
  const secondaryIds = (parties.secondary ?? []).map(s => s?.partyId)
  return [primaryId, ...secondaryIds].filter((id): id is string => !!id)
}

export const getAllAgentIds = (agents?: TPortfolioSplit[]): string[] =>
  (agents ?? []).map(split => split?.agentPartyId).filter((id): id is string => !!id)

/**
 * Set a tenant or landlord to primary.
 *
 * @param parties tenants or owners from portfolio.parties
 * @param partyId party ID to set as primary
 * @returns new portfolio parties for use in portfolio.parties.owners or portfolio.parties.tenants,
 *          or `false` if `partyId` is not currently a secondary party
 */
export const setPrimaryParty = (parties: TPortfolioPartyGroup, partyId: string): TPortfolioPartyGroup | false => {
  const primaryId = parties.primary?.partyId
  const secondaryIds = (parties.secondary ?? []).map(s => s.partyId)

  if (!secondaryIds.includes(partyId)) {
    return false
  }

  const newSecondaries = secondaryIds.filter(id => id !== partyId).map(id => ({ partyId: id }))

  return {
    ...parties,
    primary: { partyId },
    secondary: [...newSecondaries, ...(primaryId ? [{ partyId: primaryId }] : [])],
  }
}

/**
 * Remove a secondary party
 */
export const removeSecondaryParty = (parties: TPortfolioPartyGroup, partyId: string): TPortfolioPartyGroup => ({
  ...parties,
  secondary: (parties.secondary ?? []).filter(s => s.partyId !== partyId),
})

/**
 * Add a secondary party
 * @todo remove duplicates
 */
export const addSecondaryParty = (parties: TPortfolioPartyGroup, partyId: string): TPortfolioPartyGroup => ({
  ...parties,
  secondary: (parties.secondary ?? []).concat([{ partyId }]),
})

/**
 * Adds a party to parties.primary if empty, else parties.secondary
 * @todo remove duplicates
 */
export const addParty = (parties: TPortfolioPartyGroup, partyId: string): TPortfolioPartyGroup => {
  if (parties?.primary?.partyId) {
    return addSecondaryParty(parties, partyId)
  }
  return {
    ...parties,
    primary: { partyId },
  }
}
